(() => {
  if (window.kachingThemePriceSyncLoaded) return;
  window.kachingThemePriceSyncLoaded = true;

  const initialized = new WeakSet();

  function setText(node, value) {
    if (node && node.textContent.trim() !== value) node.textContent = value;
  }

  function initialize(section) {
    if (initialized.has(section)) return;
    initialized.add(section);

    let latestPricing = null;
    let priceMode = null;
    let cachedDealBlock = null;
    let configuredPriceMode = null;
    const price = () => document.getElementById(`price-${section.dataset.section}`);
    const widget = () => section.querySelector('kaching-bundles-block');
    const hasCompareOverrides = () => Boolean(section.dataset.kachingComparePrices?.trim());
    const selectedDeal = () => widget()?.querySelector(
      '.kaching-bundles__bar--selected, input[name^="kaching-bundles-deal"]:checked'
    )?.closest('.kaching-bundles__bar');

    function getConfiguredPriceMode() {
      const dealBlock = widget()?.getAttribute('deal-block');
      if (dealBlock === cachedDealBlock) return configuredPriceMode;
      cachedDealBlock = dealBlock;
      configuredPriceMode = null;
      if (!dealBlock) return null;

      try {
        const settings = JSON.parse(dealBlock);
        if (settings.updateNativePrice && settings.updateNativePriceType) {
          configuredPriceMode = settings.updateNativePriceType === 'bundle' ? 'total' : 'perItem';
        }
      } catch (error) {
        // Keep the existing price observation fallback for widgets without valid settings.
      }
      return configuredPriceMode;
    }

    function formatSavings(amount) {
      if (!section.dataset.currencyCode || !Number.isFinite(amount)) return '';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: section.dataset.currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount / 100);
    }

    function applyButtonSavings(pricing) {
      if (hasCompareOverrides()) return;
      const discounted = pricing?.discountedPrice?.amount;
      const full = pricing?.fullPrice?.amount;
      if (!Number.isFinite(discounted) || !Number.isFinite(full)) return;

      const saved = Math.max(0, full - discounted);
      const formatted = formatSavings(saved);
      const regularButton = section.querySelector('.prodBtnMainAtcc .atcSavings');
      if (regularButton) {
        regularButton.hidden = saved === 0;
        if (saved > 0 && formatted) setText(regularButton.querySelector('.atcSave'), formatted);
      }

      const smoocheButton = section.querySelector('.atcSaving');
      if (smoocheButton) {
        smoocheButton.style.display = saved > 0 && !smoocheButton.closest('button')?.disabled ? '' : 'none';
        if (saved > 0 && formatted) setText(smoocheButton.querySelector('.money'), formatted);
      }
    }

    function apply(formatted, fullFormatted, discountedAmount, fullAmount) {
      const container = price();
      if (!container || !section.contains(container)) return;

      setText(container.querySelector('.price__sale .price-item--last'), formatted);
      if (hasCompareOverrides()) return;
      const onSale = Boolean(fullFormatted && fullFormatted !== formatted);
      container.querySelector('.price')?.classList.toggle('price--on-sale', onSale);
      if (onSale) {
        setText(container.querySelector('.price__sale s.price-item--regular'), fullFormatted);
      }

      const savings = container.querySelector('.arBadgePrice #savingAmount');
      const badge = savings?.closest('.arBadgePrice');
      const smoocheBadge = container.querySelector('.badge.price__badge-sale');
      if (!onSale || !Number.isFinite(discountedAmount) || !Number.isFinite(fullAmount)) {
        if (badge) badge.style.display = 'none';
        if (smoocheBadge) smoocheBadge.style.display = 'none';
        return;
      }

      const saved = Math.max(0, fullAmount - discountedAmount);
      const percent = fullAmount ? Math.round(saved / fullAmount * 100) : 0;
      const formattedSavings = formatSavings(saved);

      if (badge) {
        badge.style.display = '';
        if (savings.closest('.percentSave')) {
          setText(savings, `${percent}%`);
        } else if (formattedSavings) {
          setText(savings, formattedSavings);
        }
      }

      if (smoocheBadge && formattedSavings) {
        smoocheBadge.style.display = '';
        if (smoocheBadge.dataset.kachingSavings !== formattedSavings ||
          smoocheBadge.dataset.kachingPercent !== String(percent)) {
          const label = document.createElement('span');
          label.textContent = ' You save ';
          const amount = document.createElement('b');
          const money = document.createElement('span');
          money.className = 'money';
          money.textContent = formattedSavings;
          amount.append(money);
          smoocheBadge.replaceChildren(label, amount, document.createTextNode(` (${percent}% OFF)`));
          smoocheBadge.dataset.kachingSavings = formattedSavings;
          smoocheBadge.dataset.kachingPercent = String(percent);
        }
      }
    }

    function restore() {
      const bar = selectedDeal();
      if (!bar) return;
      if (latestPricing) {
        applyButtonSavings(latestPricing);
        const regular = price()?.querySelector('.price__regular .price-item')?.textContent.trim();
        const total = latestPricing.discountedPrice;
        const perItem = latestPricing.discountedPricePerItem;
        if (typeof total?.formatted !== 'string' || typeof perItem?.formatted !== 'string') return;

        const configuredMode = getConfiguredPriceMode();
        if (configuredMode) {
          priceMode = configuredMode;
        } else if (total.formatted !== perItem.formatted) {
          if (regular === total.formatted) priceMode = 'total';
          if (regular === perItem.formatted) priceMode = 'perItem';
        } else if (!priceMode) {
          priceMode = 'perItem';
        }

        const discounted = priceMode === 'total' ? total : perItem;
        const full = priceMode === 'total' ? latestPricing.fullPrice : latestPricing.fullPricePerItem;
        apply(discounted.formatted, full?.formatted, discounted.amount, full?.amount);
        return;
      }

      // Mirror Kaching's own displayed price if its initial event was missed.
      const discounted = bar.querySelector('.kaching-bundles__bar-price .money') ||
        bar.querySelector('.kaching-bundles__bar-price');
      const full = bar.querySelector('.kaching-bundles__bar-full-price .money') ||
        bar.querySelector('.kaching-bundles__bar-full-price');
      const label = bar.querySelector('.kaching-bundles__bar-label');
      const fallbackSavings = label?.textContent.trim();
      if (!hasCompareOverrides() && fallbackSavings?.toLowerCase().startsWith('save ')) {
        const fallbackAmount = fallbackSavings.slice(5);
        const regularButton = section.querySelector('.prodBtnMainAtcc .atcSavings');
        if (regularButton) {
          regularButton.hidden = false;
          setText(regularButton.querySelector('.atcSave'), fallbackAmount);
        }
        const smoocheButton = section.querySelector('.atcSaving');
        if (smoocheButton) {
          smoocheButton.style.display = '';
          setText(smoocheButton.querySelector('.money'), fallbackAmount);
        }
      }
      const regular = price()?.querySelector('.price__regular .price-item')?.textContent.trim();
      if (!regular) return;
      if (getConfiguredPriceMode() === 'total') {
        priceMode = 'total';
        if (discounted) apply(discounted.textContent.trim(), full?.textContent.trim());
        return;
      }
      if (regular === discounted?.textContent.trim()) {
        priceMode = 'total';
        apply(regular, full?.textContent.trim());
      } else {
        priceMode = 'perItem';
        const container = price();
        setText(container?.querySelector('.price__sale .price-item--last'), regular);
        if (!hasCompareOverrides()) {
          const badge = container?.querySelector('.arBadgePrice');
          if (badge) badge.style.display = 'none';
          const smoocheBadge = container?.querySelector('.badge.price__badge-sale');
          if (smoocheBadge) smoocheBadge.style.display = 'none';
        }
      }
    }

    section.addEventListener('deal-bar-selected', () => {
      latestPricing = null;
    });

    section.addEventListener('variants-changed', (event) => {
      if (!event.target.closest('kaching-bundles-block')) return;
      const pricing = event.detail?.pricing;
      if (!pricing?.discountedPrice?.formatted || !pricing?.discountedPricePerItem?.formatted) return;
      latestPricing = pricing;
      restore();
    });

    let observedPrice = null;
    let observedButton = null;
    const priceObserver = new MutationObserver(restore);
    const buttonObserver = new MutationObserver(restore);
    const discoveryObserver = new MutationObserver(() => {
      const container = price();
      if (!selectedDeal() || !container) return;
      if (container !== observedPrice) {
        priceObserver.disconnect();
        observedPrice = container;
        priceObserver.observe(container, { childList: true, subtree: true, characterData: true });
      }
      const button = section.querySelector('.prodBtnMainAtcc') || section.querySelector('.atcSaving')?.closest('button');
      if (button && button !== observedButton) {
        buttonObserver.disconnect();
        observedButton = button;
        buttonObserver.observe(button, { childList: true, subtree: true, characterData: true });
      }
      restore();
    });
    discoveryObserver.observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
    const container = price();
    if (selectedDeal() && container) {
      observedPrice = container;
      priceObserver.observe(container, { childList: true, subtree: true, characterData: true });
      const button = section.querySelector('.prodBtnMainAtcc') || section.querySelector('.atcSaving')?.closest('button');
      if (button) {
        observedButton = button;
        buttonObserver.observe(button, { childList: true, subtree: true, characterData: true });
      }
      restore();
    }
  }

  function initializeAll(root = document) {
    if (root.matches?.('section[id^="MainProduct-"]')) initialize(root);
    root.querySelectorAll('section[id^="MainProduct-"]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }
  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
